/**
 * Concurrency-controlled upload queue for invoice attachments.
 *
 * Pure TypeScript — no React dependency. Communicates state changes
 * via the onItemUpdate callback so a React hook (or anything else)
 * can stay in sync.
 *
 * Usage:
 *   const queue = new UploadQueue({ onItemUpdate: (i, update) => ... });
 *   queue.add(files);
 *   queue.start();
 */
import axios from "axios";
import { createAttachment, uploadToPresignedUrl } from "../api/attachmentApi";
import {
  FILE_UPLOAD_STATUS,
  DEFAULT_ALLOWED_TYPES,
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_MAX_FILES,
  DEFAULT_CONCURRENCY,
  type FileUploadItem,
  type UploadQueueOptions,
  type UploadQueueSummary,
  type AddFilesResult,
} from "../types/attachment";

export class UploadQueue {
  private items: FileUploadItem[] = [];
  private concurrency: number;
  private maxFileSize: number;
  private maxFiles: number;
  private allowedTypes: string[];
  private onItemUpdate: (index: number, update: Partial<FileUploadItem>) => void;
  private abortControllers: Map<number, AbortController> = new Map();
  private activeCount = 0;
  private started = false;

  constructor(options: UploadQueueOptions = {}) {
    this.concurrency = options.concurrency ?? DEFAULT_CONCURRENCY;
    this.maxFileSize = options.maxFileSize ?? DEFAULT_MAX_FILE_SIZE;
    this.maxFiles = options.maxFiles ?? DEFAULT_MAX_FILES;
    this.allowedTypes = options.allowedTypes ?? [...DEFAULT_ALLOWED_TYPES];
    this.onItemUpdate = options.onItemUpdate ?? (() => {});
  }

  // --- Public API ---

  /** Validate and enqueue files. Returns count of accepted + list of rejected with reasons. */
  add(files: File[]): AddFilesResult {
    const rejected: AddFilesResult["rejected"] = [];
    let accepted = 0;

    for (const file of files) {
      // Check max files limit before per-file validation
      if (this.items.length >= this.maxFiles) {
        rejected.push({ file, reason: `Maximum ${this.maxFiles} files allowed` });
        continue;
      }

      const error = this.validate(file);
      if (error) {
        rejected.push({ file, reason: error });
        continue;
      }

      this.items.push({
        file,
        status: FILE_UPLOAD_STATUS.QUEUED,
        progress: 0,
      });
      accepted++;
    }

    // If already started, kick off processing for newly added files
    if (this.started) {
      this.processNext();
    }

    return { accepted, rejected };
  }

  /** Begin processing the queue. Can be called once — additional files added later auto-process. */
  start(): void {
    if (this.started) return;
    this.started = true;
    this.processNext();
  }

  /** Retry a failed file at the given index. */
  retry(index: number): void {
    const item = this.items[index];
    if (!item || item.status !== FILE_UPLOAD_STATUS.FAILED) return;

    this.updateItem(index, {
      status: FILE_UPLOAD_STATUS.QUEUED,
      progress: 0,
      error: undefined,
      attachmentId: undefined,
    });

    if (this.started) {
      this.processNext();
    }
  }

  /** Retry all failed files. */
  retryAllFailed(): void {
    for (let i = 0; i < this.items.length; i++) {
      if (this.items[i].status === FILE_UPLOAD_STATUS.FAILED) {
        this.retry(i);
      }
    }
  }

  /** Cancel a single file — abort if in-flight, remove from queue if queued. */
  cancel(index: number): void {
    const item = this.items[index];
    if (!item) return;

    const controller = this.abortControllers.get(index);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(index);
      this.activeCount--;
    }

    // Remove from items array
    this.items.splice(index, 1);

    // Rebuild abort controllers map since indices shifted
    const newControllers = new Map<number, AbortController>();
    for (const [key, value] of this.abortControllers) {
      const newKey = key > index ? key - 1 : key;
      newControllers.set(newKey, value);
    }
    this.abortControllers = newControllers;

    // Notify that entire list changed (caller should re-read getItems)
    this.onItemUpdate(-1, {});

    if (this.started) {
      this.processNext();
    }
  }

  /** Abort all in-flight uploads and reset queue. */
  cancelAll(): void {
    for (const controller of this.abortControllers.values()) {
      controller.abort();
    }
    this.abortControllers.clear();
    this.activeCount = 0;
    this.items = [];
    this.started = false;
    this.onItemUpdate(-1, {});
  }

  /** Get a snapshot of all items. */
  getItems(): FileUploadItem[] {
    return [...this.items];
  }

  /** Get aggregate counts. */
  getSummary(): UploadQueueSummary {
    const summary: UploadQueueSummary = { total: 0, queued: 0, uploading: 0, uploaded: 0, failed: 0 };
    for (const item of this.items) {
      summary.total++;
      if (item.status === FILE_UPLOAD_STATUS.QUEUED || item.status === FILE_UPLOAD_STATUS.CREATING) {
        summary.queued++;
      } else if (item.status === FILE_UPLOAD_STATUS.UPLOADING) {
        summary.uploading++;
      } else if (item.status === FILE_UPLOAD_STATUS.UPLOADED) {
        summary.uploaded++;
      } else if (item.status === FILE_UPLOAD_STATUS.FAILED) {
        summary.failed++;
      }
    }
    return summary;
  }

  // --- Private ---

  private validate(file: File): string | null {
    if (file.size === 0) {
      return "File is empty";
    }
    if (file.size > this.maxFileSize) {
      const maxMB = Math.round(this.maxFileSize / (1024 * 1024));
      return `File exceeds ${maxMB} MB limit`;
    }
    if (this.allowedTypes.length > 0 && !this.allowedTypes.includes(file.type)) {
      return `File type "${file.type || "unknown"}" is not allowed. Accepted: PDF, PNG, JPG`;
    }
    return null;
  }

  private processNext(): void {
    while (this.activeCount < this.concurrency) {
      const index = this.items.findIndex(
        (item) => item.status === FILE_UPLOAD_STATUS.QUEUED
      );
      if (index === -1) break;
      this.processFile(index);
    }
  }

  private async processFile(index: number): Promise<void> {
    this.activeCount++;
    // Capture the original File reference — it is the only stable identity across
    // the async lifecycle, since updateItem() replaces each item via object spread.
    // findIdx() searches by File reference so cancel() splicing the array never
    // causes async continuations to operate on the wrong item.
    const file = this.items[index].file;
    const findIdx = () => this.items.findIndex((i) => i.file === file);

    try {
      // Step 1: Create attachment record, get presigned URL.
      // Use index directly here — this is synchronous, no splice can have happened yet.
      this.updateItem(index, { status: FILE_UPLOAD_STATUS.CREATING, progress: 0 });

      const attachment = await createAttachment(file.name);

      // Re-derive index after first await — a cancel() may have spliced the array.
      let ci = findIdx();
      if (ci === -1) return; // item was cancelled while createAttachment was in-flight
      this.updateItem(ci, { attachmentId: attachment.id });

      // Step 2: Upload file bytes to presigned URL
      this.updateItem(ci, { status: FILE_UPLOAD_STATUS.UPLOADING });

      const { promise, controller } = uploadToPresignedUrl(
        attachment.url,
        file,
        (percent) => {
          const i = findIdx();
          if (i !== -1) this.updateItem(i, { progress: percent });
        },
      );
      ci = findIdx();
      if (ci !== -1) this.abortControllers.set(ci, controller);

      await promise;

      // Re-derive index after second await.
      ci = findIdx();
      if (ci !== -1) {
        this.abortControllers.delete(ci);
        this.updateItem(ci, { status: FILE_UPLOAD_STATUS.UPLOADED, progress: 100 });
      }
    } catch (err) {
      const ci = findIdx();
      if (ci !== -1) this.abortControllers.delete(ci);
      // Don't mark as failed if it was a user-initiated cancellation
      if (axios.isCancel(err)) return;
      if (ci !== -1) {
        const message = err instanceof Error ? err.message : "Upload failed";
        this.updateItem(ci, { status: FILE_UPLOAD_STATUS.FAILED, error: message });
      }
    } finally {
      this.activeCount--;
      this.processNext();
    }
  }

  private updateItem(index: number, update: Partial<FileUploadItem>): void {
    if (index >= 0 && index < this.items.length) {
      this.items[index] = { ...this.items[index], ...update } as FileUploadItem;
    }
    this.onItemUpdate(index, update);
  }
}
